import React, { useEffect, useMemo, useRef, useState } from 'react'

const parseNumber = (value) => Number.parseFloat(value) || 0
const wheelTiming = 'cubic-bezier(0.85, 0, 0.15, 1)'

const getFormatter = (locale, decimals) => new Intl.NumberFormat(locale, {
  minimumFractionDigits: decimals,
  maximumFractionDigits: decimals,
})

const getFormattedMeta = (value, locale, decimals) => {
  const formatter = getFormatter(locale, decimals)
  const parts = formatter.formatToParts(parseNumber(value))
  const integerChunks = parts.filter((part) => part.type === 'integer').map((part) => part.value)
  const integerDigits = (integerChunks.join('').match(/\d/g) || ['0'])
  const fractionDigits = (parts.find((part) => part.type === 'fraction')?.value.match(/\d/g) || [])
  const groupSeparator = parts.find((part) => part.type === 'group')?.value || ','
  const decimalSeparator = parts.find((part) => part.type === 'decimal')?.value || '.'
  const sign = parts.filter((part) => part.type === 'minusSign').map((part) => part.value).join('')
  const groupBreaks = []

  let visibleIndex = 0
  integerChunks.forEach((chunk, chunkIndex) => {
    visibleIndex += chunk.length
    if (chunkIndex < integerChunks.length - 1) {
      groupBreaks.push(visibleIndex - 1)
    }
  })

  return {
    sign,
    integerDigits,
    fractionDigits,
    groupSeparator,
    decimalSeparator,
    groupBreaks,
  }
}

const createStaticColumns = (digits, prefix) => digits.map((digit, index) => ({
  key: `${prefix}-${index}`,
  position: Number(digit),
  visible: true,
}))

const getAnimatedPosition = (prevPosition, targetDigit, direction, shouldAnimate) => {
  if (!shouldAnimate) return targetDigit

  const previousDigit = ((prevPosition % 10) + 10) % 10

  if (direction >= 0) {
    let delta = targetDigit - previousDigit
    if (delta < 0) delta += 10
    return prevPosition + delta + 10
  }

  let delta = previousDigit - targetDigit
  if (delta < 0) delta += 10
  return prevPosition - delta - 10
}

const createAnimatedColumns = (prevColumns, targetDigits, prefix, direction) => {
  const nextLength = Math.max(prevColumns.length, targetDigits.length)
  const visibleStart = nextLength - targetDigits.length

  return Array.from({ length: nextLength }, (_, index) => {
    const targetVisibleIndex = index - visibleStart
    const visible = targetVisibleIndex >= 0
    const targetDigit = visible ? Number(targetDigits[targetVisibleIndex]) : 0
    const prevColumn = prevColumns[index - (nextLength - prevColumns.length)] || null
    const prevPosition = prevColumn?.position ?? targetDigit
    const prevVisible = prevColumn?.visible ?? false
    const shouldAnimate = prevColumn && (prevVisible !== visible || (((prevPosition % 10) + 10) % 10) !== targetDigit)

    return {
      key: `${prefix}-${index}`,
      position: getAnimatedPosition(prevPosition, targetDigit, direction, shouldAnimate),
      visible,
    }
  })
}

const createStaticModel = (value, locale, decimals) => {
  const meta = getFormattedMeta(value, locale, decimals)

  return {
    value: parseNumber(value),
    meta,
    animate: false,
    integerColumns: createStaticColumns(meta.integerDigits, 'int'),
    fractionColumns: createStaticColumns(meta.fractionDigits, 'frac'),
  }
}

const createAnimatedModel = (prevModel, value, locale, decimals) => {
  const meta = getFormattedMeta(value, locale, decimals)
  const direction = parseNumber(value) >= prevModel.value ? 1 : -1

  return {
    value: parseNumber(value),
    meta,
    animate: true,
    integerColumns: createAnimatedColumns(prevModel.integerColumns, meta.integerDigits, 'int', direction),
    fractionColumns: createAnimatedColumns(prevModel.fractionColumns, meta.fractionDigits, 'frac', direction),
  }
}

const DigitWheel = ({ animate, column, duration }) => {
  const minPosition = Math.min(column.position, 0) - 10
  const maxPosition = Math.max(column.position, 0) + 10
  const digits = []

  for (let position = minPosition; position <= maxPosition; position += 1) {
    digits.push({
      key: position,
      value: ((position % 10) + 10) % 10,
    })
  }

  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        overflow: 'hidden',
        height: '1em',
        width: column.visible ? '0.58em' : '0',
        opacity: column.visible ? 1 : 0,
        transition: animate ? `width ${duration}ms ${wheelTiming}, opacity ${Math.min(duration, 220)}ms ${wheelTiming}` : 'none',
      }}
    >
      <span
        style={{
          display: 'flex',
          flexDirection: 'column',
          transform: `translateY(-${column.position - minPosition}em)`,
          transition: animate ? `transform ${duration}ms ${wheelTiming}` : 'none',
        }}
      >
        {digits.map((digit) => (
          <span
            key={digit.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '1em',
              lineHeight: '1em',
            }}
          >
            {digit.value}
          </span>
        ))}
      </span>
    </span>
  )
}

const AnimatedNumber = ({
  value,
  duration = 2000,
  decimals = 2,
  locale = 'en-US',
  className,
}) => {
  const safeValue = useMemo(() => parseNumber(value), [value])
  const [model, setModel] = useState(() => createStaticModel(safeValue, locale, decimals))
  const timeoutRef = useRef(null)
  const initializedRef = useRef(false)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    if (!initializedRef.current) {
      initializedRef.current = true
      setModel(createStaticModel(safeValue, locale, decimals))
      return undefined
    }

    setModel((prevModel) => createAnimatedModel(prevModel, safeValue, locale, decimals))

    timeoutRef.current = setTimeout(() => {
      setModel(createStaticModel(safeValue, locale, decimals))
    }, duration)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [decimals, duration, locale, safeValue])

  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        lineHeight: 1,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {model.meta.sign ? <span>{model.meta.sign}</span> : null}

      {model.integerColumns.map((column, index) => {
        const visibleLength = model.meta.integerDigits.length
        const visibleStart = model.integerColumns.length - visibleLength
        const visibleIndex = index - visibleStart
        const shouldRenderGroup = visibleIndex >= 0 && model.meta.groupBreaks.includes(visibleIndex)

        return (
          <React.Fragment key={column.key}>
            <DigitWheel animate={model.animate} column={column} duration={duration} />
            {shouldRenderGroup ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                {model.meta.groupSeparator}
              </span>
            ) : null}
          </React.Fragment>
        )
      })}

      {model.fractionColumns.length > 0 ? (
        <>
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            {model.meta.decimalSeparator}
          </span>
          {model.fractionColumns.map((column) => (
            <DigitWheel key={column.key} animate={model.animate} column={column} duration={duration} />
          ))}
        </>
      ) : null}
    </span>
  )
}

export default AnimatedNumber
